/**
 * Dashboard — live KPIs, portfolio charts, and quick actions.
 * Fetches real data from ingestion + validation APIs and auto-refreshes.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileStack, AlertTriangle, ShieldAlert,
  TrendingUp, ArrowRight,
  Upload, BarChart3,
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts';
import { AppShell } from '@/acc-ui/components/AppShell';
import { Card, SectionLabel, ErrorState } from '@/acc-ui/primitives/Layout';
import { Button } from '@/acc-ui/primitives/Button';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/auth/use-current-user';
import { ingestionApi } from '@/api/ingestion-api';
import { validationApi } from '@/api/validation-api';
import { usePermission } from '@/auth/use-permission';
import { INGESTION_UPLOAD, VALIDATION_VIEW } from '@loanaudit/shared';

// M3 / brand colors used by the charts (recharts needs literal colors).
const C = {
  processing: '#d97706', // amber-600
  parsed: '#212f43',     // tertiary navy
  validated: '#059669',  // emerald-600
  failed: '#ba1a1a',     // error
  primary: '#97144D',    // brand burgundy
  primaryLight: '#C23B6E',
  critical: '#ba1a1a',
  high: '#d97706',
  medium: '#f59e0b',
  low: '#cbd5e1',
  grid: '#dcbfc5',       // outline-variant
  axis: '#564146',       // on-surface-variant
};

export function DashboardPage() {
  const { user } = useCurrentUser();
  const { has } = usePermission();
  const navigate = useNavigate();
  const firstName = user?.displayName?.split(' ')[0] ?? 'there';
  const canUpload = has(INGESTION_UPLOAD);
  const canSeeValidation = has(VALIDATION_VIEW);

  // Fetch loan files (first page, recent). Auto-refreshes for near-real-time KPIs.
  const loanFilesQuery = useQuery({
    queryKey: ['dashboard-loan-files'],
    queryFn: () => ingestionApi.list({ page: 1, pageSize: 100 }),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Fetch findings
  const findingsQuery = useQuery({
    queryKey: ['dashboard-findings'],
    queryFn: () => validationApi.listFindings({ page: 1, pageSize: 200, latestOnly: true }),
    enabled: canSeeValidation,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Loading / error status. findingsQuery only counts when the user can see
  // validation (otherwise it's disabled and never loads). Previously a failed
  // fetch rendered as genuine zeros — indistinguishable from an empty account.
  const isLoading = loanFilesQuery.isLoading || (canSeeValidation && findingsQuery.isLoading);
  const isError = loanFilesQuery.isError || (canSeeValidation && findingsQuery.isError);
  const retry = () => {
    void loanFilesQuery.refetch();
    if (canSeeValidation) void findingsQuery.refetch();
  };

  // Compute KPIs
  const kpis = useMemo(() => {
    const files = loanFilesQuery.data?.items ?? [];
    const findings = findingsQuery.data?.items ?? [];

    const total = files.length;
    const validated = files.filter((f: { state: string }) => f.state === 'validated').length;
    const validationFailed = files.filter((f: { state: string }) => f.state === 'validation_failed').length;
    const parsing = files.filter((f: { state: string }) => ['parsing_in_progress', 'accepted', 'received'].includes(f.state)).length;
    const parsed = files.filter((f: { state: string }) => f.state === 'parsed').length;

    // Severity KPIs reflect OPEN findings only — once a finding is resolved it
    // no longer "needs attention", so it must drop out of the Critical/High card
    // and the severity chips (not just the Open count).
    const openOnly = findings.filter((f: { status: string }) => f.status === 'open');
    const openFindings = openOnly.length;
    const resolvedFindings = findings.filter((f: { status: string }) => f.status !== 'open').length;

    const criticalFindings = openOnly.filter((f: { severity: string }) => f.severity === 'critical' || f.severity === 'high').length;
    const critical = openOnly.filter((f: { severity: string }) => f.severity === 'critical').length;
    const high = openOnly.filter((f: { severity: string }) => f.severity === 'high').length;
    const medium = openOnly.filter((f: { severity: string }) => f.severity === 'medium').length;
    const low = openOnly.filter((f: { severity: string }) => f.severity === 'low' || f.severity === 'info').length;

    return {
      total, validated, validationFailed, parsing, parsed,
      openFindings, criticalFindings, resolvedFindings,
      critical, high, medium, low,
      totalFindings: findings.length,
    };
  }, [loanFilesQuery.data, findingsQuery.data]);

  // Portfolio-by-state donut data
  const portfolio = useMemo(() => {
    return [
      { name: 'Processing', value: kpis.parsing, color: C.processing },
      { name: 'Parsed', value: kpis.parsed, color: C.parsed },
      { name: 'Validated', value: kpis.validated, color: C.validated },
      { name: 'Failed', value: kpis.validationFailed, color: C.failed },
    ].filter((d) => d.value > 0);
  }, [kpis]);

  // Weekly activity — received vs validated per day, last 7 days
  const weekly = useMemo(() => {
    const files = loanFilesQuery.data?.items ?? [];
    const days: { key: string; day: string; received: number; validated: number }[] = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({ key, day: d.toLocaleDateString(undefined, { weekday: 'short' }), received: 0, validated: 0 });
    }
    const idx = new Map(days.map((d) => [d.key, d]));
    const dayKey = (iso?: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : null);

    for (const f of files as Array<{ state: string; receivedAt?: string | null; updatedAt?: string | null }>) {
      const rk = dayKey(f.receivedAt);
      if (rk && idx.has(rk)) idx.get(rk)!.received += 1;
      if (f.state === 'validated') {
        const vk = dayKey(f.updatedAt);
        if (vk && idx.has(vk)) idx.get(vk)!.validated += 1;
      }
    }
    return days;
  }, [loanFilesQuery.data]);

  // 7-day intake sparkline series (for the Loan Files KPI card)
  const intakeSeries = useMemo(() => weekly.map((d) => ({ day: d.day, v: d.received })), [weekly]);

  return (
    <AppShell crumb="Workspace" title="Dashboard">
      {/* Greeting */}
      <section>
        <h1 className="font-display text-display font-bold tracking-tight text-on-surface">
          Good to see you, {firstName}
        </h1>
      </section>

      {isError ? (
        <Card>
          <ErrorState
            title="Could not load your dashboard"
            description="We couldn't reach the audit service. Your data is safe — this is a connection issue."
            action={<Button onClick={retry}>Try again</Button>}
          />
        </Card>
      ) : isLoading ? (
        <DashboardSkeleton />
      ) : (
      <>
      {/* KPI cards */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          icon={<FileStack className="w-[18px] h-[18px]" />}
          iconColor="text-primary"
          valueColor="text-primary-container"
          label="Loan Files Audited"
          value={kpis.total}
          sub={kpis.total > 0 ? `${kpis.validated} validated · ${kpis.parsing > 0 ? kpis.parsing + ' processing' : kpis.validationFailed + ' failed'}` : 'Upload your first loan file'}
          chart={kpis.total > 0 ? <IntakeSparkline data={intakeSeries} /> : undefined}
        />
        <KpiCard
          icon={<AlertTriangle className="w-[18px] h-[18px]" />}
          iconColor="text-amber-600"
          label="Findings Open"
          value={kpis.openFindings}
          sub={kpis.totalFindings > 0 ? `${kpis.resolvedFindings} resolved · ${kpis.totalFindings} total` : 'No audits run yet'}
          chart={kpis.totalFindings > 0 ? <SeverityChips kpis={kpis} /> : undefined}
        />
        <KpiCard
          icon={<ShieldAlert className="w-[18px] h-[18px]" />}
          iconColor="text-error"
          label="Critical / High"
          value={kpis.criticalFindings}
          sub={kpis.criticalFindings > 0 ? `${kpis.critical} critical · ${kpis.high} high severity` : 'No critical issues'}
          chart={kpis.criticalFindings > 0 ? <SplitBar a={kpis.critical} b={kpis.high} aColor={C.critical} bColor={C.high} /> : undefined}
        />
        <KpiCard
          icon={<TrendingUp className="w-[18px] h-[18px]" />}
          iconColor="text-emerald-600"
          label="Pass Rate"
          value={kpis.validated > 0 ? `${Math.round((kpis.validated / Math.max(1, kpis.validated + kpis.validationFailed)) * 100)}%` : '—'}
          sub={kpis.validated > 0 ? `${kpis.validated} of ${kpis.validated + kpis.validationFailed} completed successfully` : 'Baseline pending'}
          chart={kpis.validated > 0 ? <ProgressBar pct={Math.round((kpis.validated / Math.max(1, kpis.validated + kpis.validationFailed)) * 100)} /> : undefined}
        />
      </section>

      {/* Quick actions — moved up so it's visible without scrolling */}
      <Card>
        <SectionLabel>Quick Actions</SectionLabel>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {canUpload && (
            <ActionButton
              icon={<Upload className="w-4 h-4" />}
              label="Upload Loan File"
              sub="Loan number and LOS file"
              onClick={() => navigate('/loan-files/new')}
            />
          )}
          <ActionButton
            icon={<BarChart3 className="w-4 h-4" />}
            label="View All Findings"
            sub={`${kpis.openFindings} open across all files`}
            onClick={() => navigate('/findings')}
          />
        </div>
      </Card>

      {/* Charts row — portfolio donut + weekly activity.
          On desktop (lg+) the row flexes to fill the leftover viewport height
          so the dashboard fits on one screen at 100% zoom without scrolling.
          Below lg it falls back to fixed heights and the page scrolls. */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:flex-1 lg:min-h-0 lg:grid-rows-1">
        <Card className="lg:col-span-2 lg:h-full lg:flex lg:flex-col lg:min-h-0">
          <SectionLabel>Portfolio by State</SectionLabel>
          {portfolio.length > 0 ? (
            <>
              {/* Centered donut with an HTML label overlay — keeps the total
                  dead-center regardless of legend width. */}
              <div className="relative mt-1 h-[160px] lg:h-auto lg:flex-1 lg:min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={portfolio}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="56%"
                      outerRadius="82%"
                      paddingAngle={2}
                      stroke="none"
                    >
                      {portfolio.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<DonutTooltip total={kpis.total} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="font-display text-title font-bold leading-none text-on-surface tabular-nums">
                    {kpis.total}
                  </span>
                  <span className="text-micro text-on-surface-variant">files</span>
                </div>
              </div>
              {/* Custom HTML legend */}
              <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1.5">
                {portfolio.map((d) => (
                  <span key={d.name} className="flex items-center gap-1.5 text-micro text-on-surface-variant tabular-nums">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                    {d.name} · {d.value}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <EmptyChart label="No loan files yet" />
          )}
        </Card>

        <Card className="lg:col-span-3 lg:h-full lg:flex lg:flex-col lg:min-h-0">
          <SectionLabel>Weekly Activity</SectionLabel>
          <p className="text-micro text-on-surface-variant mt-0.5">Files received vs. validated · last 7 days</p>
          {kpis.total > 0 ? (
            <div className="mt-1 h-[176px] lg:h-auto lg:flex-1 lg:min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekly} margin={{ top: 4, right: 4, left: -18, bottom: 0 }} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                  <XAxis dataKey="day" tickLine={false} axisLine={{ stroke: C.grid }} tick={{ fontSize: 11, fill: C.axis }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: C.axis }} width={28} />
                  <Tooltip cursor={{ fill: 'rgba(151,20,77,0.06)' }} contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${C.grid}` }} />
                  <Legend iconType="circle" iconSize={8} formatter={(value: string) => <span className="text-caption text-on-surface-variant">{value}</span>} />
                  <Bar dataKey="received" name="Received" fill={C.primaryLight} radius={[3, 3, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="validated" name="Validated" fill={C.validated} radius={[3, 3, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyChart label="No activity yet" />
          )}
        </Card>
      </section>
      </>
      )}
    </AppShell>
  );
}

// Skeleton shown on first load so a slow fetch isn't mistaken for an empty
// account (KPIs used to render 0 while loading).
function DashboardSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-hidden>
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 h-[140px]">
            <div className="h-3 w-24 rounded bg-surface-variant" />
            <div className="mt-6 h-8 w-16 rounded bg-surface-variant" />
            <div className="mt-3 h-2 w-full rounded bg-surface-variant/70" />
          </div>
        ))}
      </section>
      <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 h-[88px]" />
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-surface-container-lowest border border-outline-variant rounded-xl h-[240px]" />
        <div className="lg:col-span-3 bg-surface-container-lowest border border-outline-variant rounded-xl h-[240px]" />
      </section>
    </div>
  );
}

// -------------------------------------------------------------------------
// Subcomponents
// -------------------------------------------------------------------------

function KpiCard({ icon, iconColor, valueColor = 'text-on-surface', label, value, sub, chart }: {
  icon: React.ReactNode;
  iconColor: string;
  valueColor?: string;
  label: string;
  value: number | string;
  sub: string;
  chart?: React.ReactNode;
}) {
  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 flex flex-col justify-between gap-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-micro font-semibold text-on-surface-variant uppercase tracking-[0.06em]">
          {label}
        </span>
        <span className={cn('shrink-0', iconColor)}>{icon}</span>
      </div>
      <div>
        <div className={cn('font-display text-title font-bold leading-none tabular-nums mb-1', valueColor)}>
          {value}
        </div>
        <div className="text-caption text-on-surface-variant">{sub}</div>
        {chart && <div className="mt-2.5">{chart}</div>}
      </div>
    </div>
  );
}

function ActionButton({ icon, label, sub, onClick }: {
  icon: React.ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-outline-variant hover:border-primary hover:bg-secondary-container/30 transition-all text-left group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-primary shrink-0">{icon}</span>
        <div className="min-w-0">
          <div className="text-label font-semibold text-on-surface">{label}</div>
          <div className="text-caption text-on-surface-variant">{sub}</div>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-on-surface-variant group-hover:text-primary transition-colors shrink-0" />
    </button>
  );
}

// --- Mini in-card infographics ---

function IntakeSparkline({ data }: { data: { day: string; v: number }[] }) {
  return (
    <div className="h-9">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <Tooltip
            cursor={{ fill: 'transparent' }}
            contentStyle={{ fontSize: 11, padding: '2px 6px', borderRadius: 6, border: `1px solid ${C.grid}` }}
            labelFormatter={(l) => `${l}`}
            formatter={(v) => [v as number, 'received']}
          />
          <Bar dataKey="v" fill={C.primaryLight} radius={[2, 2, 0, 0]} maxBarSize={10} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SeverityChips({ kpis }: { kpis: { critical: number; high: number; medium: number; low: number } }) {
  const chips = [
    { n: kpis.critical, label: 'Critical', color: C.critical },
    { n: kpis.high, label: 'High', color: C.high },
    { n: kpis.medium, label: 'Medium', color: C.medium },
    { n: kpis.low, label: 'Low', color: C.low },
  ].filter((c) => c.n > 0);
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {chips.map((c) => (
        <span key={c.label} className="flex items-center gap-1.5 text-micro text-on-surface-variant tabular-nums">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
          {c.n} {c.label}
        </span>
      ))}
    </div>
  );
}

function SplitBar({ a, b, aColor, bColor }: { a: number; b: number; aColor: string; bColor: string }) {
  const total = Math.max(1, a + b);
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden bg-surface-variant">
      {a > 0 && <div style={{ width: `${(a / total) * 100}%`, backgroundColor: aColor }} title={`Critical: ${a}`} />}
      {b > 0 && <div style={{ width: `${(b / total) * 100}%`, backgroundColor: bColor }} title={`High: ${b}`} />}
    </div>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 rounded-full overflow-hidden bg-surface-variant">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: C.validated }} />
    </div>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div className="h-[176px] flex items-center justify-center text-caption text-on-surface-variant">
      {label}
    </div>
  );
}

interface TooltipEntry { name: string; value: number; payload: { color: string } }
function DonutTooltip({ active, payload, total }: { active?: boolean; payload?: TooltipEntry[]; total: number }) {
  const p = payload?.[0];
  if (!active || !p) return null;
  const pct = total > 0 ? Math.round((p.value / total) * 100) : 0;
  return (
    <div className="rounded-lg border border-outline-variant bg-surface-container-lowest px-2.5 py-1.5 shadow-sm">
      <div className="flex items-center gap-1.5 text-caption text-on-surface">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.payload.color }} />
        <span className="font-semibold">{p.name}</span>
        <span className="text-on-surface-variant tabular-nums">{p.value} · {pct}%</span>
      </div>
    </div>
  );
}
